import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  adminUser as mockAdminUser,
  classes,
  guruUser as mockGuruUser,
  questionBank,
  schoolWideReports,
  students,
  teacherExams,
  teacherResults,
  teachers,
} from "../data/adminData";

const isDev = typeof window !== "undefined" && window.location.hostname === "localhost";

export const useCBTStore = create(
  persist(
    (set) => ({
      adminUser: null,
      currentPage: "login",
      teacherExams,
      teacherResults,
      editingTeacherExam: null,
      students,
      teachers,
      questionBank,
      classes,
      schoolWideReports,
      loading: false,
      theme: "light",
      language: "id",

      loginStaff: async ({ email, password, remember }) => {
        set({ loading: true });
        try {
          const response = await fetch("/api/auth/login-guru", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
          });
          if (!response.ok) {
            const json = await response.json().catch(() => ({}));
            const msg = json?.error?.message || json?.message || "Login gagal.";
            throw new Error(msg);
          }
          const json = await response.json();
          const data = json.data;
          localStorage.setItem("accessToken", data.accessToken);
          localStorage.setItem("refreshToken", data.refreshToken);
          set({
            adminUser: { ...data.user, remember },
            currentPage: data.user.role === "ADMIN" ? "admin-dashboard" : "guru-dashboard",
            loading: false,
          });
        } catch (err) {
          if (isDev) {
            if (password.length < 6) {
              set({ loading: false });
              throw new Error("Password minimal 6 karakter.");
            }
            const lower = email.trim().toLowerCase();
            if (lower === mockAdminUser.email.toLowerCase()) {
              set({
                adminUser: { ...mockAdminUser, remember },
                currentPage: "admin-dashboard",
                loading: false,
              });
              return;
            }
            if (lower === mockGuruUser.email.toLowerCase()) {
              set({
                adminUser: { ...mockGuruUser, remember },
                currentPage: "guru-dashboard",
                loading: false,
              });
              return;
            }
            set({ loading: false });
            throw new Error("Email tidak ditemukan. Gunakan admin@example.sch.id atau sari.guru@example.sch.id");
          }
          set({ loading: false });
          throw err;
        }
      },

      logout: () =>
        set({
          adminUser: null,
          currentPage: "login",
        }),

      setCurrentPage: (page) => set({ currentPage: page }),
      setAdminUser: (adminUser) => set({ adminUser }),
      setStudents: (students) => set({ students }),
      updateStudent: (studentId, patch) =>
        set((state) => ({
          students: state.students.map((student) =>
            student.id === studentId ? { ...student, ...patch } : student,
          ),
        })),
      deleteStudent: (studentId) =>
        set((state) => ({
          students: state.students.filter((student) => student.id !== studentId),
        })),
      addTeacher: (teacher) =>
        set((state) => ({
          teachers: [
            { ...teacher, id: Date.now(), status: teacher.status ?? "aktif" },
            ...state.teachers,
          ],
        })),
      updateTeacher: (teacherId, patch) =>
        set((state) => ({
          teachers: state.teachers.map((t) => (t.id === teacherId ? { ...t, ...patch } : t)),
        })),
      deleteTeacher: (teacherId) =>
        set((state) => ({
          teachers: state.teachers.filter((t) => t.id !== teacherId),
        })),
      addClass: (classroom) =>
        set((state) => ({
          classes: [
            { ...classroom, id: `kelas-${Date.now()}`, jumlahSiswa: Number(classroom.jumlahSiswa) || 0 },
            ...state.classes,
          ],
        })),
      updateClass: (classId, patch) =>
        set((state) => ({
          classes: state.classes.map((c) => (c.id === classId ? { ...c, ...patch } : c)),
        })),
      addQuestionBankItem: (question) =>
        set((state) => ({
          questionBank: [
            { ...question, id: `b-${Date.now()}`, digunakan: Number(question.digunakan) || 0 },
            ...state.questionBank,
          ],
        })),
      updateQuestionBankItem: (questionId, patch) =>
        set((state) => ({
          questionBank: state.questionBank.map((q) =>
            q.id === questionId ? { ...q, ...patch } : q,
          ),
        })),
      deleteQuestionBankItem: (questionId) =>
        set((state) => ({
          questionBank: state.questionBank.filter((q) => q.id !== questionId),
        })),
      setTeacherExams: (teacherExams) => set({ teacherExams }),
      setEditingTeacherExam: (editingTeacherExam) => set({ editingTeacherExam }),
      addTeacherExam: (exam) =>
        set((state) => ({
          teacherExams: [
            {
              ...exam,
              id: `g-${Date.now()}`,
              status: exam.status ?? "draft",
              totalSiswa: 0,
              submitted: 0,
              rataRata: 0,
            },
            ...state.teacherExams,
          ],
        })),
      updateTeacherExam: (examId, patch) =>
        set((state) => ({
          teacherExams: state.teacherExams.map((e) =>
            e.id === examId ? { ...e, ...patch } : e,
          ),
        })),
      deleteTeacherExam: (examId) =>
        set((state) => ({
          teacherExams: state.teacherExams.filter((e) => e.id !== examId),
        })),
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
    }),
    {
      name: "cbt-admin-preferences",
      partialize: (state) => ({
        theme: state.theme,
        language: state.language,
        adminUser: state.adminUser?.remember ? state.adminUser : null,
      }),
    },
  ),
);